import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle, Building2, CheckCircle2, MapPin, Phone,
} from 'lucide-react';
import {
  Alert, Button, Card, Spinner, SystemPage, TextInput, phone as phoneLib,
} from '@parsina/framework';
import { C } from '@parsina/framework/tokens';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { STACK } from '../../constants/density';
import usePageTitle from '../../hooks/usePageTitle';
import { bffQuery, bffMutation } from '../../lib/bffClient.js';
import { getToken, parseJwt } from '../../lib/auth.js';
import AuthShell from '../../components/auth/AuthShell.jsx';
import InviteSkeleton from './InviteSkeleton.jsx';
import { clinicTypeLabel } from '../Clinic/Clinic.shared';
import { describeError, RECOVERY } from '../Onboarding/errors.js';

/**
 * «دعوت‌نامه کلینیک» — the QR / link landing.
 *
 * Routed OUTSIDE `ClinicContextProvider` (see `AppShell`), because it is
 * answerable before anything is known about membership: it is how a person
 * with no clinic at all arrives. That has one consequence worth stating,
 * because it shapes the code below — this screen cannot ask the provider
 * anything, so the one question it does need answered («do they already have a
 * clinic to go back to?») is asked directly, after the join, and only to
 * decide which buttons to draw.
 *
 * ── The success state used to be a dead end ─────────────────────────────────
 *
 * «درخواست شما ثبت شد» was two lines of text and nothing else: no way to the
 * start page, no way back to a panel for someone who already had one. The
 * person scanned a code at a reception desk, got a sentence, and had to guess.
 * Every terminal state on this screen now carries a way forward.
 *
 * ── نام و نام خانوادگی (PM-0453) ─────────────────────────────────────────────
 *
 * A phone sign-up carries no name, and the request used to reach «همکاران» as
 * «بدون نام». The person now types both halves; the BFF writes them to their
 * Keycloak account and files the request under them. The fields start from the
 * name the account already has, so someone who has one only confirms it.
 */

const CLINIC_BY_CODE = `
  query ClinicByCode($code: String!) {
    clinicByInvitationCode(code: $code) {
      id name officePhone address clinicType city
    }
  }
`;

const JOIN_CLINIC = `
  mutation JoinClinic($code: String!, $firstName: String, $lastName: String) {
    joinClinicByInvitationCode(code: $code, firstName: $firstName, lastName: $lastName) { id name }
  }
`;

/** The longest half of a name the BFF accepts (`JOIN_NAME_MAX`). */
const NAME_MAX = 60;

/** The account's name as the sign-in token carries it — where the fields start. */
function accountName() {
  const claims = parseJwt(getToken() || '') || {};
  return { first: claims.given_name || '', last: claims.family_name || '' };
}

/** The person's own memberships, asked without naming a clinic. */
const MY_MEMBERSHIPS = `
  query InviteMemberships {
    myMemberships { clinicId slug clinicName enterable }
  }
`;

/** One fact about the clinic, with its glyph. */
function Fact({ icon: Icon, children, ltr }) {
  if (!children) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 6 }}>
      <Icon size={14} color={C.textMuted} aria-hidden="true" style={{ marginTop: 3, flexShrink: 0 }} />
      <span
        style={{ fontSize: '0.78125rem', color: C.textSecondary, lineHeight: 1.7, minWidth: 0, ...(ltr ? LTR_TEXT.style : null) }}
        dir={ltr ? LTR_TEXT.dir : undefined}
      >
        {children}
      </span>
    </div>
  );
}

export default function InviteScreen() {
  const { t, formatList } = useTranslation();
  const { code } = useParams();
  const navigate = useNavigate();

  const [clinic, setClinic] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [joining, setJoining] = React.useState(false);
  const [loadError, setLoadError] = React.useState(null);
  const [joinError, setJoinError] = React.useState(null);
  const [joined, setJoined] = React.useState(false);
  const [panelRef, setPanelRef] = React.useState('');
  const [name, setName] = React.useState(accountName);
  const [nameErrors, setNameErrors] = React.useState({});

  usePageTitle(t('auth.invite.title'));

  React.useEffect(() => {
    if (!code) { setLoadError({ message: t('errors.invite.invalidCode') }); setLoading(false); return undefined; }
    let alive = true;
    bffQuery(CLINIC_BY_CODE, { code }, 'ClinicByCode')
      .then((r) => {
        if (!alive) return;
        const c = r?.clinicByInvitationCode;
        if (!c) setLoadError({ message: t('errors.invite.notFoundOrExpired') });
        else setClinic(c);
      })
      .catch((e) => { if (alive) setLoadError(describeError(e, 'join', t)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code, t]);

  /**
   * Does this person already have a panel to go back to?
   *
   * Asked only after the outcome is known, and only to decide whether to draw
   * a second button. A failure here hides that button and is never surfaced —
   * it would be a second, irrelevant error on the screen that just delivered
   * the one that matters.
   */
  const lookUpPanel = React.useCallback(async () => {
    try {
      const r = await bffQuery(MY_MEMBERSHIPS, {}, 'InviteMemberships');
      const mine = (r?.myMemberships || []).find((m) => m.enterable);
      if (mine) setPanelRef(mine.slug || mine.clinicId);
    } catch { /* the button simply does not appear */ }
  }, []);

  const editName = (key) => (e) => {
    const { value } = e.target;
    setName((n) => ({ ...n, [key]: value }));
    setNameErrors((errs) => ({ ...errs, [key]: '' }));
  };

  const handleJoin = async () => {
    const firstName = name.first.trim();
    const lastName = name.last.trim();
    const tooLong = t('errors.invite.nameTooLong');
    const required = t('errors.invite.nameRequired');
    const errs = {
      first: !firstName ? required : (firstName.length > NAME_MAX ? tooLong : ''),
      last: !lastName ? required : (lastName.length > NAME_MAX ? tooLong : ''),
    };
    if (errs.first || errs.last) { setNameErrors(errs); return; }
    setJoining(true);
    setJoinError(null);
    try {
      await bffMutation(JOIN_CLINIC, { code, firstName, lastName }, 'JoinClinic');
      setJoined(true);
      lookUpPanel();
    } catch (e) {
      const described = describeError(e, 'join', t);
      setJoinError(described);
      if (described.recovery === RECOVERY.GO_TO_CLINIC && described.clinicId) {
        setPanelRef(described.clinicId);
      }
      setJoining(false);
    }
  };

  const goStart = { key: 'start', label: t('clinic.gate.goToStart'), onClick: () => navigate('/start') };
  const goPanel = panelRef
    ? { key: 'panel', label: t('clinic.start.backToPanel'), variant: 'outline', onClick: () => navigate(`/c/${panelRef}/`) }
    : null;

  // ── Loading ───────────────────────────────────────────────────────────────
  // The same shell as the loaded page, with the clinic card in blocks: the
  // shell paints once and the card fills in place (no spinner, no jump).
  if (loading) {
    return (
      <AuthShell width="md" testId="invite" title={t('auth.invite.title')} subtitle={t('auth.invite.subtitle')}>
        <InviteSkeleton />
      </AuthShell>
    );
  }

  // ── The code is not a code ────────────────────────────────────────────────
  if (loadError) {
    return (
      <AuthShell width="md" testId="invite" title={t('auth.invite.title')}>
        <SystemPage
          tone="danger"
          icon={<AlertCircle size={26} aria-hidden="true" />}
          title={t('auth.invite.invalidTitle')}
          body={loadError.message}
          testId="invite-invalid"
          // No «وارد کردن کد دیگر»: «شروع» no longer takes a typed code. A
          // colleague is added by their number at the clinic's «همکاران».
          actions={[goStart]}
        />
      </AuthShell>
    );
  }

  // ── The request is in ─────────────────────────────────────────────────────
  if (joined) {
    return (
      <AuthShell width="md" testId="invite" title={t('auth.invite.title')}>
        <SystemPage
          tone="info"
          icon={<CheckCircle2 size={26} aria-hidden="true" />}
          title={t('auth.invite.requestReceived')}
          body={t('auth.invite.awaitingApproval')}
          testId="invite-joined"
          actions={[goStart, goPanel].filter(Boolean)}
        />
      </AuthShell>
    );
  }

  // ── The clinic, and the decision ──────────────────────────────────────────
  const blocking = joinError && joinError.recovery !== RECOVERY.RETRY;

  return (
    <AuthShell
      width="md"
      testId="invite"
      title={t('auth.invite.title')}
      subtitle={t('auth.invite.subtitle')}
    >
      {clinic ? (
        <Card style={{ padding: 18, background: C.bg }} data-testid="invite-clinic">
          <div style={{ fontSize: '1.03125rem', fontWeight: 800, color: C.textPrimary, marginBottom: 4 }}>
            <bdi>{clinic.name}</bdi>
          </div>
          {/* The type is a CODE on the wire («general», or a legacy «CLINIC»).
              Printing it verbatim, which is what this screen used to do, showed
              a person their clinic's type as an English enum. */}
          <Fact icon={Building2}>{clinicTypeLabel(clinic.clinicType, t)}</Fact>
          {/* `formatList`, not a literal «، »: the separator between a city and
              a street is «، » in Persian and «, » in English, and baking one of
              them into source is how an English page ends up punctuated in
              Arabic. */}
          <Fact icon={MapPin}>
            {formatList([clinic.city, clinic.address].filter(Boolean), 'unit')}
          </Fact>
          {/* Formatted at the DISPLAY EDGE, through the kit's one read-path renderer.
              `officePhone` is canonical E.164 since «مشخصات کلینیک» started writing it,
              and this is a PUBLIC page — a person deciding whether they recognise the
              clinic should read «۰۲۱۸۸۰۰۰۰۰۰», not «+982188000000». `forDisplay` is
              landline-tolerant by construction (an office line is the normal case here,
              and `toNational` alone would refuse to render one) and hands back a value it
              cannot parse — a legacy row, a note somebody typed — exactly as stored,
              rather than blanking it. Showing nothing is worse than showing what we have. */}
          <Fact icon={Phone} ltr>
            {phoneLib.forDisplay(clinic.officePhone)}
          </Fact>
        </Card>
      ) : null}

      {blocking ? null : (
        <div
          style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: STACK.regular, marginTop: STACK.regular,
          }}
          data-testid="invite-name"
        >
          <TextInput
            label={t('auth.invite.firstName')}
            required
            value={name.first}
            onChange={editName('first')}
            error={nameErrors.first || undefined}
            maxLength={NAME_MAX}
            autoComplete="given-name"
            disabled={joining}
            data-testid="invite-first-name"
          />
          <TextInput
            label={t('auth.invite.lastName')}
            required
            value={name.last}
            onChange={editName('last')}
            error={nameErrors.last || undefined}
            helperText={nameErrors.last ? undefined : t('auth.invite.nameHelper')}
            maxLength={NAME_MAX}
            autoComplete="family-name"
            disabled={joining}
            data-testid="invite-last-name"
          />
        </div>
      )}

      <Alert variant="info" style={{ marginTop: STACK.regular }}>
        {t('auth.invite.approvalNotice')}
      </Alert>

      {joinError ? (
        <Alert variant="error" role="alert" style={{ marginTop: STACK.regular }} data-testid="invite-error">
          {joinError.message}
        </Alert>
      ) : null}

      <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
        {/* A refusal that will not change on a second press retires the submit
            button rather than leaving it live: pressing «ارسال درخواست» again
            after «شما هم‌اکنون عضو این کلینیک هستید» cannot do anything but
            repeat itself. */}
        {blocking ? (
          <>
            {panelRef ? (
              <Button onClick={() => navigate(`/c/${panelRef}/`)} data-testid="invite-go-panel">
                {t('clinic.start.backToPanel')}
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => navigate('/start')} data-testid="invite-go-start">
              {t('clinic.gate.goToStart')}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleJoin}
              disabled={joining}
              aria-busy={joining || undefined}
              data-testid="invite-submit"
              style={{ flex: 1, minWidth: 180 }}
            >
              {joining ? <Spinner size={14} color="#fff" label={t('common.state.sending')} /> : null}
              {joining ? t('common.state.sending') : t('auth.invite.submit')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/start')} disabled={joining} data-testid="invite-cancel">
              {t('common.actions.cancel')}
            </Button>
          </>
        )}
      </div>
    </AuthShell>
  );
}
